import React, { useContext, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import manPic from '../Image/userM.png'
import womanPic from '../Image/userW.png'
import { FamilyContext } from "../Context/FamilyHeadContext"
import { API_URL, doApiGet } from "../services/apiService"
import {formatFullName} from "../utils/personUtils"
import LetterNavigation from '../components/LetterNavigation'

const GenerationCount = () => {
    const { gedId, generation } = useParams()
    const [descendants, setDescendants] = useState([])
    const [groupedDescendants, setGroupedDescendants] = useState({})
    const [currentLetter, setCurrentLetter] = useState('')
    const [totalCount, setTotalCount] = useState(0)
    const [isLoading, setIsLoading] = useState(true)
    const {t, i18n} = useTranslation()
    const { pathPrefix, isMainSite, currentFamilyHead } = useContext(FamilyContext)

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const getRelevantFamilyName = (familyName) => {
        if (!familyName || familyName === '?' || familyName.trim() === '') {
            return t('generation.misc')
        }

        const match = familyName.match(/\([^)]+\)\s*([^\s]+)/)
        return match ? match[1] : familyName
    }

    useEffect(() => {
        const fetchGenerationData = async () => {
            setIsLoading(true)
            try {
                const apiPath = `${API_URL}/people/descendants/${gedId}/${generation}/${isMainSite}`
                const data = await doApiGet(apiPath)

                if (data && Array.isArray(data.descendants)) {
                    const sortedDescendants = [...data.descendants].sort((a, b) => {
                        const familyNameA = getRelevantFamilyName(a.familyName)
                        const familyNameB = getRelevantFamilyName(b.familyName)

                        if (familyNameA === t('generation.misc') && familyNameB !== t('generation.misc')) return 1
                        if (familyNameB === t('generation.misc') && familyNameA !== t('generation.misc')) return -1

                        const isAHebrew = /^[\u0590-\u05FF]/.test(familyNameA)
                        const isBHebrew = /^[\u0590-\u05FF]/.test(familyNameB)

                        if (isAHebrew && !isBHebrew) return -1
                        if (!isAHebrew && isBHebrew) return 1

                        return familyNameA.localeCompare(familyNameB, 'he')
                    })

                    const grouped = sortedDescendants.reduce((acc, person) => {
                        const relevantName = getRelevantFamilyName(person.familyName)
                        const firstLetter = relevantName === t('generation.misc') ? t('generation.misc') : relevantName.charAt(0)

                        if (!acc[firstLetter]) {
                            acc[firstLetter] = []
                        }
                        acc[firstLetter].push(person)
                        return acc
                    }, {})

                    setGroupedDescendants(grouped)
                    setDescendants(sortedDescendants)

                    const letters = Object.keys(grouped).filter(letter => letter !== t('generation.misc'))
                    if (letters.length > 0) {
                        setCurrentLetter(letters[0])
                    } else {
                        setCurrentLetter(t('generation.misc'))
                    }

                    setTotalCount(data.size || data.descendants.length)
                }
            } catch (error) {
                console.error(t('generation.error'), error)
            } finally {
                setIsLoading(false)
            }
        }

        if (gedId && generation && isMainSite) {
            fetchGenerationData()
        }
    }, [gedId, generation, isMainSite, t])

    const handleLetterClick = (letter) => {
        setCurrentLetter(letter)
        const element = document.getElementById(`letter-section-${letter}`)
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
        }
    }

    if (isLoading) {
        return (
            <div className="bg-[#f8f5f1] rounded-xl p-4 lg:p-6">
                <div className="flex items-center justify-center p-4">
                    <div className="w-6 h-6 border-2 border-[#937c5d] border-t-transparent rounded-full animate-spin"></div>
                </div>
            </div>
        )
    }

    return (
        <div className="bg-[#f8f5f1] rounded-xl p-4 lg:p-6">
            <h2 className="text-xl text-[#937c5d] font-heebo mb-4 border-b border-[#e6dfd4] pb-3">
                {t('generation.others')} {generation} ל{currentFamilyHead.name}
                <span className="text-sm mr-2">
                    ({totalCount}{t('generation.members')})
                </span>
            </h2>

            <LetterNavigation
                onLetterClick={handleLetterClick}
                currentLetter={currentLetter}
                availableLetters={Object.keys(groupedDescendants)}
            />

            <div className="space-y-8 mt-4">
                {Object.entries(groupedDescendants)
                    .filter(([letter]) => currentLetter === letter)
                    .map(([letter, personList]) => (
                        <div key={letter} id={`letter-section-${letter}`}>
                            <div className="text-2xl font-bold text-[#937c5d] mb-4 bg-[#f8f5f1] py-2 border-b-2 border-[#e6dfd4]">
                                {letter}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {personList.map((person) => {
                                    const imageUrl = person.image?.length > 0
                                        ? `${API_URL}/image/download/${encodeURIComponent(person.image)}`
                                        : null

                                    return (
                                        <div key={person._id}
                                             className="bg-white rounded-lg p-3 flex items-center gap-3 hover:shadow-md transition-shadow">
                                            <div className="w-12 h-12 bg-white rounded-lg overflow-hidden flex-shrink-0 border-2 border-[#e6dfd4]">
                                                {imageUrl ? (
                                                    <img
                                                        crossOrigin="use-credentials"
                                                        src={imageUrl}
                                                        alt={`${formatFullName(person, i18n['language'])}`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        {person.sex === 'M'
                                                            ? <img crossOrigin="use-credentials" src={manPic}
                                                                   alt={t('generation.malePhotoAlt')}/>
                                                            : <img crossOrigin="use-credentials" src={womanPic}
                                                                   alt={t('generation.femalePhotoAlt')}/>
                                                        }
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-grow">
                                                <div
                                                    className="font-medium text-[#4a3f30]"> {formatFullName(person, i18n['language'])}</div>
                                                <Link to={`${pathPrefix}person/${person.gedId}`}
                                                      className="text-sm text-[#937c5d] hover:text-[#4a3f30] flex items-center gap-1">
                                                    {t('generation.viewProfile')}
                                                    <ArrowLeft className="w-4 h-4"/>
                                                </Link>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    ))}
            </div>
        </div>
    )
}

export default GenerationCount